import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Download, Bell, BellOff, Smartphone, Check } from 'lucide-react';

/**
 * Componente para gerenciar instalação PWA e notificações push
 */
export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // Verificar se já está instalado
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Capturar evento de instalação
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    // Verificar permissão de notificações
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Detectar instalação
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setShowPrompt(false);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
  };

  const handleNotifications = async () => {
    if (!('Notification' in window)) {
      alert('Seu navegador não suporta notificações');
      return;
    }

    if (Notification.permission === 'granted') {
      setNotificationsEnabled(true);
      return;
    }

    const permission = await Notification.requestPermission();
    setNotificationsEnabled(permission === 'granted');

    if (permission === 'granted') {
      // Mostrar notificação de teste
      new Notification('DOEM Acaiaca', {
        body: 'Notificações ativadas! Você receberá alertas de novas publicações.',
        icon: 'https://customer-assets.emergentagent.com/job_321423f8-7787-4cc3-8d65-76e41f468946/artifacts/fsh7vmmd_Bras%C3%A3o%20Alta%20Qualidade%20Acaiaca.png'
      });
    }
  };

  if (!showPrompt && isInstalled) return null;

  return (
    <div className="fixed bottom-20 right-4 z-50 flex flex-col gap-2">
      {/* Botão de instalação */}
      {deferredPrompt && !isInstalled && (
        <Button
          onClick={handleInstall}
          className="bg-red-700 hover:bg-red-800 text-white shadow-lg flex items-center gap-2"
          size="sm"
        >
          <Smartphone className="w-4 h-4" />
          Instalar App
        </Button>
      )}

      {/* Botão de notificações */}
      {!notificationsEnabled && 'Notification' in window && (
        <Button
          onClick={handleNotifications}
          variant="outline"
          className="bg-white shadow-lg flex items-center gap-2"
          size="sm"
        >
          <Bell className="w-4 h-4" />
          Ativar Alertas
        </Button>
      )}

      {/* Indicador de instalado */}
      {isInstalled && (
        <div className="bg-green-100 text-green-800 px-3 py-2 rounded-lg text-sm flex items-center gap-2">
          <Check className="w-4 h-4" />
          App Instalado
        </div>
      )}
    </div>
  );
}

/**
 * Hook para verificar status do PWA
 */
export function usePWA() {
  const [isInstalled, setIsInstalled] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    // Verificar modo standalone
    setIsInstalled(window.matchMedia('(display-mode: standalone)').matches);

    // Monitorar conexão
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { isInstalled, isOnline };
}

/**
 * Componente de banner offline
 */
export function OfflineBanner() {
  const { isOnline } = usePWA();

  if (isOnline) return null;

  return (
    <div className="fixed top-0 left-0 right-0 bg-yellow-500 text-yellow-900 text-center py-2 text-sm z-50">
      📡 Você está offline. Alguns recursos podem não estar disponíveis.
    </div>
  );
}

export default PWAInstallPrompt;
